import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import { XMarkIcon, PhotoIcon, FaceSmileIcon, SparklesIcon } from '@heroicons/react/24/outline';
import { StarIcon } from '@heroicons/react/24/outline';
import { StarIcon as StarIconSolid } from '@heroicons/react/24/solid';
import RatingDisplay from './RatingDisplay';
import imageCompression from 'browser-image-compression';
import { API_BASE_URL } from '../../utils/constants';

const WriteReviewModal = ({ isOpen, onClose, onSubmit, venue }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  
  const [rating, setRating] = useState(0);
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [photos, setPhotos] = useState([]);
  const [photoPreview, setPhotoPreview] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showConfetti, setShowConfetti] = useState(false);
  const [showCategoryRatings, setShowCategoryRatings] = useState(false);
  const [categoryRatings, setCategoryRatings] = useState({ food: 0, service: 0, atmosphere: 0 });
  const [wifiTested, setWifiTested] = useState(false);
  const [wifiResults, setWifiResults] = useState(null);
  const [compressionProgress, setCompressionProgress] = useState('');

  // ✅ Prevent memory leaks
  const isMountedRef = useRef(true);
  const submittingRef = useRef(false);
  
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const ratingLabels = {
    1: t('review.ratingPoor'), 2: t('review.ratingFair'), 3: t('review.ratingGood'),
    4: t('review.ratingVeryGood'), 5: t('review.ratingExcellent'),
  };

  // ✅ FIXED: Better compression with mobile optimization
  const validateAndPrepareFile = async (file, index, total) => {
    const MAX_SIZE = 3 * 1024 * 1024; // 3MB after compression
    
    const allowedTypes = [
      'image/jpeg',
      'image/jpg', 
      'image/png',
      'image/gif',
      'image/webp',
      'image/heic',
      'image/heif'
    ];
    
    const allowedExtensions = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'heic', 'heif'];
    const extension = file.name.split('.').pop()?.toLowerCase() || '';
    
    if (!allowedTypes.includes(file.type.toLowerCase()) && !allowedExtensions.includes(extension)) {
      throw new Error(t('review.notValidImageFormat', { name: file.name }));
    }

    try {
      if (isMountedRef.current) {
        setCompressionProgress(t('review.compressingProgress', { index: index + 1, total }));
      }
      
      console.log(`🔄 Compressing ${file.name} (${(file.size / 1024 / 1024).toFixed(2)}MB)...`);
      
      // ✅ Mobile-optimized compression settings
      const options = {
        maxSizeMB: 1.5, // Reduced for mobile
        maxWidthOrHeight: 1600, // Reduced resolution for mobile
        useWebWorker: true,
        fileType: 'image/jpeg',
        initialQuality: 0.75, // Slightly lower quality for smaller files
        alwaysKeepResolution: false // Allow resolution reduction
      };
      
      const compressedFile = await imageCompression(file, options);
      
      console.log(`✅ Compressed to ${(compressedFile.size / 1024 / 1024).toFixed(2)}MB`);
      
      if (compressedFile.size > MAX_SIZE) {
        // ✅ Second pass compression if still too large
        console.log('⚠️ File still large, applying aggressive compression...');
        const aggressiveOptions = {
          maxSizeMB: 1,
          maxWidthOrHeight: 1200,
          useWebWorker: true,
          fileType: 'image/jpeg',
          initialQuality: 0.6
        };
        const recompressed = await imageCompression(compressedFile, aggressiveOptions);
        
        if (recompressed.size > MAX_SIZE) {
          throw new Error(t('review.imageTooLarge', { name: file.name }));
        }
        
        const finalFile = new File(
          [recompressed], 
          file.name.replace(/\.[^/.]+$/, '.jpg'),
          { type: 'image/jpeg' }
        );
        
        return finalFile;
      }
      
      const finalFile = new File(
        [compressedFile], 
        file.name.replace(/\.[^/.]+$/, '.jpg'),
        { type: 'image/jpeg' }
      );
      
      return finalFile;
    } catch (error) {
      console.error('❌ Compression error:', error);
      throw new Error(t('review.failedToProcessFile', { name: file.name, message: error.message }));
    } finally {
      if (isMountedRef.current) {
        setCompressionProgress('');
      }
    }
  };

  useEffect(() => {
    if (location.state?.wifiResults && location.state?.returnTo === 'review') {
      setWifiResults(location.state.wifiResults);
      setWifiTested(true);
      
      if (location.state.currentRating) {
        setRating(location.state.currentRating);
        setShowCategoryRatings(location.state.currentRating > 0);
      }
      if (location.state.currentTitle) setTitle(location.state.currentTitle);
      if (location.state.currentContent) setContent(location.state.currentContent);
      if (location.state.currentCategoryRatings) setCategoryRatings(location.state.currentCategoryRatings);
      
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, navigate, location.pathname]);

  const handleRatingChange = (newRating) => {
    setRating(newRating);
    setShowCategoryRatings(newRating > 0);
  };

  const handleCategoryRatingChange = (cat, val) =>
    setCategoryRatings((p) => ({ ...p, [cat]: val }));

  const totalHaloPoints = () => {
    const catPoints = Object.values(categoryRatings).reduce((a, b) => a + b, 0) * 2;
    const wifiPoints = wifiTested ? 10 : 0;
    return 5 + catPoints + wifiPoints;
  };

  // ✅ FIXED: Prevent duplicate submissions with ref
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // ✅ Double-check with ref to prevent any race conditions
    if (isSubmitting || submittingRef.current) {
      console.log('⏸️ Already submitting, ignoring duplicate submission');
      return;
    }
    
    if (!rating || rating === 0 || isNaN(rating)) {
      alert(t('review.pleaseSelectRating'));
      return;
    }

    const venueId = venue?.id || venue?._id;

    if (!venueId) {
      alert(t('review.venueIdMissing'));
      console.error('❌ Venue object:', venue);
      return;
    }

    console.log(`📤 Submitting review with ${photos.length} photo(s)`);
    
    // ✅ Set both state and ref
    setIsSubmitting(true);
    submittingRef.current = true;
    
    try {
      const token = localStorage.getItem('venue_app_token');
      const region = venue?.region || 'th';
      
      const validRating = parseInt(rating, 10);
      if (isNaN(validRating) || validRating < 1 || validRating > 5) {
        throw new Error('Invalid rating value');
      }
      
      // ✅ Create fresh FormData
      const formData = new FormData();
      formData.append('venueId', venueId);
      formData.append('rating', validRating.toString());
      formData.append('title', title.trim() || t('review.ratingExperienceSuffix', { rating: ratingLabels[validRating] }));
      formData.append('comment', content.trim());
      
      const tags = [
        categoryRatings.food ? `food:${categoryRatings.food}` : '',
        categoryRatings.service ? `service:${categoryRatings.service}` : '',
        categoryRatings.atmosphere ? `atmosphere:${categoryRatings.atmosphere}` : '',
        wifiTested ? 'wifi_tested' : ''
      ].filter(Boolean);
      
      formData.append('tags', JSON.stringify(tags));
      
      // ✅ FIXED: Properly append photos with unique handling
      console.log(`📸 Appending ${photos.length} photos to FormData`);
      
      // ✅ Append each photo individually with proper field name
      for (let i = 0; i < photos.length; i++) {
        const file = photos[i];
        formData.append('photos', file, file.name);
        console.log(`  - Photo ${i + 1}: ${file.name} (${(file.size / 1024).toFixed(2)}KB)`);
      }

      // ✅ Verify FormData contents
      let photoCount = 0;
      for (let pair of formData.entries()) {
        if (pair[0] === 'photos') {
          photoCount++;
        }
      }
      console.log(`✅ Total photos in FormData: ${photoCount}`);

      const response = await fetch(`${API_BASE_URL}/reviews`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'x-region': region
        },
        body: formData
      });

      let errorMessage = t('review.failedToSubmitReview');
      let responseData = null;

      try {
        responseData = await response.json();
        console.log('📥 Server response:', responseData);
      } catch (e) {
        console.error('❌ Failed to parse response:', e);
      }

      if (!response.ok) {
        if (responseData && responseData.message) {
          errorMessage = responseData.message;
        } else {
          errorMessage = `Server error: ${response.status} ${response.statusText}`;
        }
        throw new Error(errorMessage);
      }

      console.log(`✅ Review submitted successfully with ${photoCount} photos`);

      // ✅ Only update state if component is still mounted
      if (isMountedRef.current) {
        setShowConfetti(true);
        setShowSuccess(true);
        
        // ✅ Clean up and close after delay
        setTimeout(() => {
          if (isMountedRef.current) {
            setShowSuccess(false);
            setShowConfetti(false);
            handleClose();
            if (onSubmit) onSubmit(responseData?.data || responseData);
          }
        }, 2000);
      }
      
    } catch (error) {
      console.error('❌ Error submitting review:', error);
      
      // ✅ Only show alert if mounted
      if (isMountedRef.current) {
        alert(t('review.failedToSubmitReviewWithReason', { message: error.message }));
        setIsSubmitting(false);
        submittingRef.current = false;
      }
    }
  };

  const handleClose = () => {
    // Clean up object URLs
    photoPreview.forEach(preview => {
      if (preview.url) {
        URL.revokeObjectURL(preview.url);
      }
    });
    
    // Reset all state
    setRating(0);
    setCategoryRatings({ food: 0, service: 0, atmosphere: 0 });
    setShowCategoryRatings(false);
    setTitle('');
    setContent('');
    setPhotos([]);
    setPhotoPreview([]);
    setWifiTested(false);
    setWifiResults(null);
    setShowSuccess(false);
    setShowConfetti(false);
    setIsSubmitting(false);
    setCompressionProgress('');
    submittingRef.current = false; // ✅ Reset ref
    onClose();
  };

  // ✅ FIXED: Better photo handling with progress and error recovery
  const handlePhotoUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    const MAX_FILES = 20;

    if (photos.length + files.length > MAX_FILES) {
      alert(t('review.maxPhotosAllowed', { max: MAX_FILES, remaining: MAX_FILES - photos.length }));
      e.target.value = '';
      return;
    }

    try {
      console.log(`📸 Processing ${files.length} photo(s)...`);
      
      const validatedFiles = [];
      const newPreviews = [];
      
      // ✅ Process files sequentially to avoid memory issues on mobile
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        
        try {
          const compressedFile = await validateAndPrepareFile(file, i, files.length);
          validatedFiles.push(compressedFile);
          
          // Create preview
          const preview = {
            url: URL.createObjectURL(compressedFile),
            id: `${Math.random()}-${Date.now()}-${i}`,
            name: compressedFile.name
          };
          newPreviews.push(preview);
          
        } catch (error) {
          console.error(`❌ Failed to process file ${i + 1}:`, error);
          alert(t('review.failedToProcessFile', { name: file.name, message: error.message }));
        }
      }

      // ✅ Only update state if we have valid files
      if (validatedFiles.length > 0 && isMountedRef.current) {
        setPhotos(prev => [...prev, ...validatedFiles].slice(0, MAX_FILES));
        setPhotoPreview(prev => [...prev, ...newPreviews].slice(0, MAX_FILES));
        console.log(`✅ Added ${validatedFiles.length} compressed photo(s). Total: ${photos.length + validatedFiles.length}`);
      }
      
    } catch (error) {
      console.error('❌ Photo upload error:', error);
      alert(error.message || t('review.failedToAddSomePhotos'));
    } finally {
      e.target.value = '';
      if (isMountedRef.current) {
        setCompressionProgress('');
      }
    }
  };

  const removePhoto = (index) => {
    if (photoPreview[index]?.url) {
      URL.revokeObjectURL(photoPreview[index].url);
    }
    setPhotos(prev => prev.filter((_, i) => i !== index));
    setPhotoPreview(prev => prev.filter((_, i) => i !== index));
  };

  const handleWifiTest = () => {
    navigate('/speed-test', { 
      state: { 
        returnTo: 'review',
        venueId: venue?.id || venue?._id,
        currentRating: rating,
        currentTitle: title,
        currentContent: content,
        currentCategoryRatings: categoryRatings,
        showCategoryRatings: showCategoryRatings
      }
    });
  };

  const Confetti = () => (
    showConfetti && (
      <div style={{ position: 'fixed', inset: 0, pointerEvents: 'none', zIndex: 2500 }}>
        {[...Array(40)].map((_, i) => (
          <motion.div
            key={`confetti-${i}-${Date.now()}`}
            initial={{ x: Math.random() * window.innerWidth, y: -20, scale: 0 }}
            animate={{ y: window.innerHeight + 20, scale: [0, 1, 0] }}
            transition={{ duration: 2 + Math.random() * 2 }}
            style={{
              position: 'absolute',
              width: '6px',
              height: '6px',
              background: '#fbbf24',
              borderRadius: '50%'
            }}
          />
        ))}
      </div>
    )
  );

  const CategoryRating = ({ label, rating, onChange }) => (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 0' }}>
      <span style={{ fontSize: 14, fontWeight: 600, color: '#374151' }}>{label}</span>
      <div style={{ display: 'flex', gap: 4 }}>
        {[1, 2, 3, 4, 5].map((star) => (
          <button 
            key={`${label}-star-${star}`} 
            onClick={() => onChange(star)} 
            style={{ background: 'none', border: 0, cursor: 'pointer', padding: 4 }}
            type="button"
          >
            {star <= rating ? (
              <StarIconSolid style={{ width: 22, height: 22, color: '#fbbf24' }} />
            ) : (
              <StarIcon style={{ width: 22, height: 22, color: '#d1d5db' }} />
            )}
          </button>
        ))}
      </div>
    </div>
  );

  if (!isOpen) return null;

  return (
    <AnimatePresence mode="wait">
      {isOpen && (
        <motion.div
          key="modal-overlay"
          initial={{ opacity: 0 }} 
          animate={{ opacity: 1 }} 
          exit={{ opacity: 0 }}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000,
            display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20
          }}
          onClick={handleClose}
        >
          <motion.div
            key="modal-content"
            initial={{ scale: 0.9, opacity: 0 }} 
            animate={{ scale: 1, opacity: 1 }} 
            exit={{ scale: 0.9, opacity: 0 }}
            onClick={(e) => e.stopPropagation()}
            style={{ 
              background: '#fff', 
              borderRadius: 20, 
              width: '100%', 
              maxWidth: 500, 
              maxHeight: '85vh', 
              display: 'flex', 
              flexDirection: 'column' 
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '20px 24px', borderBottom: '1px solid #f3f4f6' }}>
              <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>{t('review.writeAReview')}</h2>
              <button onClick={handleClose} style={{ border: 0, background: 'none', cursor: 'pointer' }} type="button">
                <XMarkIcon style={{ width: 20, height: 20, color: '#6b7280' }} />
              </button>
            </div>

            <div style={{ flex: 1, overflowY: 'auto', padding: 24 }}>
              <form onSubmit={handleSubmit}>
                <div style={{ display: 'flex', gap: 12, marginBottom: 24, background: '#f9fafb', padding: 12, borderRadius: 12 }}>
                  <div style={{ width: 48, height: 48, background: '#ea580c', borderRadius: 12, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff' }}>☕</div>
                  <div>
                    <div style={{ fontWeight: 600 }}>{venue?.name || t('venue.venueFallback')}</div>
                    <div style={{ fontSize: 14, color: '#6b7280' }}>
                      {venue?.city || t('review.cityFallback')}, {venue?.state || venue?.region || t('review.regionFallback')}
                    </div>
                  </div>
                </div>

                <div style={{ textAlign: 'center', marginBottom: 24 }}>
                  <div style={{ fontWeight: 600, marginBottom: 12 }}>{t('review.howWouldYouRate')}</div>
                  <RatingDisplay rating={rating} interactive onChange={handleRatingChange} size="xlarge" />
                  {rating > 0 && <div style={{ marginTop: 12, fontWeight: 600, color: '#ea580c' }}>{ratingLabels[rating]}</div>}
                </div>

                <AnimatePresence mode="wait">
                  {showCategoryRatings && (
                    <motion.div
                      key="category-ratings"
                      initial={{ height: 0, opacity: 0 }} 
                      animate={{ height: 'auto', opacity: 1 }} 
                      exit={{ height: 0, opacity: 0 }}
                      style={{ border: '1px solid #f1f5f9', borderRadius: 12, marginBottom: 24, overflow: 'hidden' }}
                    >
                      <div style={{ padding: 20 }}>
                        <CategoryRating label={t('review.categoryFood')} rating={categoryRatings.food} onChange={(v) => handleCategoryRatingChange('food', v)} />
                        <CategoryRating label={t('review.categoryService')} rating={categoryRatings.service} onChange={(v) => handleCategoryRatingChange('service', v)} />
                        <CategoryRating label={t('review.categoryAtmosphere')} rating={categoryRatings.atmosphere} onChange={(v) => handleCategoryRatingChange('atmosphere', v)} />
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>

                <input
                  value={title} 
                  onChange={(e) => setTitle(e.target.value)} 
                  placeholder={t('review.reviewTitlePlaceholder')}
                  style={{ width: '100%', padding: 12, marginBottom: 20, border: '2px solid #e5e7eb', borderRadius: 12 }}
                  disabled={isSubmitting}
                />

                <textarea
                  value={content} 
                  onChange={(e) => setContent(e.target.value)} 
                  rows={4}
                  placeholder={t('review.tellOthersPlaceholder')}
                  style={{ width: '100%', padding: 12, marginBottom: 20, border: '2px solid #e5e7eb', borderRadius: 12, resize: 'vertical' }}
                  disabled={isSubmitting}
                />

                <div style={{ marginBottom: 24 }}>
                  <label style={{ display: 'block', fontWeight: 600, marginBottom: 12 }}>
                    {t('review.addPhotosOptionalCount', { count: photos.length })}
                    {compressionProgress && (
                      <span style={{ marginLeft: 8, fontSize: 12, color: '#ea580c' }}>{compressionProgress}</span>
                    )}
                  </label>
                  
                  {photoPreview.length > 0 && (
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(80px, 1fr))', gap: 8, marginBottom: 12 }}>
                      {photoPreview.map((preview, index) => (
                        <div key={preview.id} style={{ position: 'relative', width: '100%', paddingBottom: '100%', borderRadius: 8, overflow: 'hidden' }}>
                          <img 
                            src={preview.url}
                            alt={t('review.previewAlt', { num: index + 1 })}
                            style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', objectFit: 'cover' }} 
                          />
                          <button
                            type="button"
                            onClick={() => removePhoto(index)}
                            disabled={isSubmitting}
                            style={{ position: 'absolute', top: 4, right: 4, border: 0, background: 'rgba(0,0,0,0.7)', borderRadius: '50%', width: 20, height: 20, color: '#fff', cursor: isSubmitting ? 'not-allowed' : 'pointer', fontSize: 14 }}
                          >
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                  )}

                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, padding: 12, border: '2px dashed #d1d5db', borderRadius: 12, cursor: (photos.length >= 20 || isSubmitting || compressionProgress) ? 'not-allowed' : 'pointer', opacity: (photos.length >= 20 || isSubmitting || compressionProgress) ? 0.5 : 1 }}>
                    <PhotoIcon style={{ width: 20, height: 20, color: '#6b7280' }} />
                    <span>
                      {compressionProgress ? compressionProgress : photos.length >= 20 ? t('review.maxPhotosReached', { max: 20 }) : t('review.addPhotos')}
                    </span>
                    <input 
                      type="file" 
                      accept="image/*,image/heic,image/heif,.heic,.heif" 
                      multiple 
                      onChange={handlePhotoUpload}
                      disabled={photos.length >= 20 || isSubmitting || !!compressionProgress}
                      style={{ display: 'none' }} 
                    />
                  </label>
                  
                  {photos.length > 0 && (
                    <div style={{ fontSize: 12, color: '#6b7280', marginTop: 8 }}>
                      {t('review.photosSelectedCount', { count: photos.length })}
                    </div>
                  )}
                </div>

                <motion.button
                  type="button"
                  onClick={handleWifiTest}
                  disabled={rating === 0 || isSubmitting}
                  whileHover={{ scale: (rating === 0 || isSubmitting) ? 1 : 1.02 }}
                  style={{
                    width: '100%', 
                    padding: 16, 
                    marginBottom: 16,
                    background: (rating === 0 || isSubmitting) ? '#e5e7eb' : '#D9641F',
                    color: (rating === 0 || isSubmitting) ? '#9ca3af' : '#fff',
                    border: 0,
                    borderRadius: 12, 
                    fontWeight: 600, 
                    cursor: (rating === 0 || isSubmitting) ? 'not-allowed' : 'pointer',
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center', 
                    gap: 10
                  }}
                >
                  <span style={{ fontSize: '20px' }}>🛜</span>
                  <span>{wifiTested ? t('review.wifiTestedHp') : t('review.testWifiHp')}</span>
                  {wifiTested && (
                    <motion.span
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      style={{ fontSize: '16px', marginLeft: '4px' }}
                    >
                      ✅
                    </motion.span>
                  )}
                </motion.button>

                {wifiTested && wifiResults && (
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    style={{
                      backgroundColor: '#f0f9ff',
                      border: '2px solid #0ea5e9',
                      borderRadius: 12,
                      padding: 16,
                      marginBottom: 16
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                      <span style={{ fontSize: '18px' }}>🛜</span>
                      <span style={{ fontWeight: 600, color: '#0c4a6e' }}>{t('review.wifiTestResults')}</span>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                      <div style={{ textAlign: 'center', padding: 8, backgroundColor: 'rgba(16, 185, 129, 0.1)', borderRadius: 8 }}>
                        <div style={{ fontSize: 12, color: '#065f46', marginBottom: 4 }}>{t('venue.downloadSpeed')}</div>
                        <div style={{ fontSize: 16, fontWeight: 700, color: '#10b981' }}>
                          {wifiResults.download} Mbps
                        </div>
                      </div>

                      <div style={{ textAlign: 'center', padding: 8, backgroundColor: 'rgba(59, 130, 246, 0.1)', borderRadius: 8 }}>
                        <div style={{ fontSize: 12, color: '#1e40af', marginBottom: 4 }}>{t('venue.uploadSpeed')}</div>
                        <div style={{ fontSize: 16, fontWeight: 700, color: '#3b82f6' }}>
                          {wifiResults.upload} Mbps
                        </div>
                      </div>
                    </div>

                    <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 8, fontSize: 12, color: '#64748b' }}>
                      <span>{t('review.pingLabel', { ping: wifiResults.ping })}</span>
                      <span>{t('review.qualityLabel', { quality: wifiResults.downloadQuality?.label || t('review.facetOpt.good') })}</span>
                    </div>
                  </motion.div>
                )}

                <motion.button
                  type="submit" 
                  disabled={rating === 0 || isSubmitting || !!compressionProgress}
                  whileHover={{ scale: (rating === 0 || isSubmitting || compressionProgress) ? 1 : 1.02 }}
                  style={{
                    width: '100%', 
                    padding: 16,
                    background: (rating === 0 || isSubmitting || compressionProgress) ? '#e5e7eb' : '#ea580c',
                    color: (rating === 0 || isSubmitting || compressionProgress) ? '#9ca3af' : '#fff',
                    border: 0, 
                    borderRadius: 12, 
                    fontWeight: 700, 
                    cursor: (rating === 0 || isSubmitting || compressionProgress) ? 'not-allowed' : 'pointer',
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center', 
                    gap: 8,
                    opacity: isSubmitting ? 0.7 : 1
                  }}
                >
                  {isSubmitting ? t('review.submitting') : compressionProgress ? compressionProgress : (
                    <>
                      <FaceSmileIcon style={{ width: 20, height: 20 }} />
                      {t('review.submitReviewButton')}
                    </>
                  )}
                </motion.button>
              </form>
            </div>
          </motion.div>
        </motion.div>
      )}

      <AnimatePresence mode="wait">
        {showSuccess && (
          <motion.div
            key="success-overlay"
            initial={{ opacity: 0 }} 
            animate={{ opacity: 1 }} 
            exit={{ opacity: 0 }}
            style={{ 
              position: 'fixed', 
              inset: 0, 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center', 
              pointerEvents: 'none', 
              zIndex: 2000
            }}
          >
            <motion.div
              key="success-content"
              initial={{ scale: 0 }} 
              animate={{ scale: 1 }} 
              exit={{ scale: 0 }}
              style={{ 
                background: '#f5933d', 
                borderRadius: 24, 
                padding: 32, 
                textAlign: 'center', 
                maxWidth: 380, 
                pointerEvents: 'auto' 
              }}
            >
              <SparklesIcon style={{ width: 50, height: 50, color: '#10b981', marginBottom: 16 }} />
              <h3 style={{ margin: 0, marginBottom: 16, fontSize: 24, fontWeight: 800 }}>{t('review.reviewSubmittedExclaim')}</h3>
              <div style={{ background: '#fef3c7', padding: 16, borderRadius: 12, fontWeight: 700, color: '#92400e' }}>
                🏆 {t('review.haloPointsEarned', { count: totalHaloPoints() })}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      <Confetti />
    </AnimatePresence>
  );
};

export default WriteReviewModal;